/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { Box, Paper, PaperProps, Table, TableBody, TableCell, TableCellBaseProps, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { GripDotsVerticalIcon } from "../../FontAwesome";

const ITEM_TYPE = "dnd-item";

type TableCellElement = React.ReactElement<TableCellBaseProps>

export type DragAndDropTableHeadProps = { children: TableCellElement | TableCellElement[] };
export const DragAndDropTableHead = (props: DragAndDropTableHeadProps) => <>{props.children}</>;

export type DragAndDropTableBodyProps = DragAndDropTableHeadProps;
export const DragAndDropTableBody = DragAndDropTableHead;

export type DropType = "above" | "below";

export type DragAndDropTableProps<T extends {}> = {
    head: React.ReactElement<DragAndDropTableHeadProps>;
    renderBody: (item: T, idx: number, data: T[]) => React.ReactElement<DragAndDropTableBodyProps>;
    dnd: boolean;
    data: T[];
    onDrop: (dragItem: T, dropItem: T, dropType: DropType) => void;
    children?: React.ReactNode;
    sx?: PaperProps["sx"];
    variant?: PaperProps["variant"];
};

type DragAndDropTableRowProps<T extends {}> =
    & { item: T; idx: number; }
    & Pick<DragAndDropTableProps<T>, "renderBody" | "dnd" | "data" | "onDrop">;

function DragAndDropTableRow<T extends {}>(props: DragAndDropTableRowProps<T>) {
    const tableRowRef = React.useRef<HTMLElement | null>(null);
    const [border, setBorder] = React.useState<"top" | "bottom" | null>(null)

    function getDropType(clientOffset: XYCoord | null): DropType | null {
        if (tableRowRef.current !== null && clientOffset !== null) {
            const rect = tableRowRef.current.getBoundingClientRect()
            const middleY = (rect.bottom - rect.top) / 2
            const hoverClientY = clientOffset.y - rect.top;
            return hoverClientY < middleY ? "above" : "below";
        }

        return null;
    }

    const { item, onDrop } = props;

    const [, drag, preview] = useDrag(() => ({
        type: ITEM_TYPE,
        item,
    }), [item]);

    const [{ isOver }, drop] = useDrop(() => ({
        accept: ITEM_TYPE,
        drop: (dragItem: T, monitor) => {
            const dropType = getDropType(monitor.getClientOffset())
            if (dropType !== null) {
                props.onDrop(dragItem, item, dropType);
            }
        },
        hover: (_, monitor) => {
            const dropType = getDropType(monitor.getClientOffset());
            setBorder(dropType !== null ? (dropType === "above" ? "top" : "bottom") : null);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }), [onDrop, item]);

    return (
        <TableRow
            ref={el => {
                drop(el);
                preview(el);
                tableRowRef.current = el;
            }}
            sx={[
                (isOver && border === "top") && {
                    [`& .${tableCellClasses.root}`]: {
                        borderTop: theme => `2px solid ${theme.palette.secondary.main}`,
                    },
                },
                (isOver && border === "bottom") && {
                    [`& .${tableCellClasses.root}`]: {
                        borderBottom: theme => `2px solid ${theme.palette.secondary.main}`,
                    },
                },
            ]}
            hover
        >
            {props.dnd && (
                <TableCell>
                    <Box ref={drag} sx={{ cursor: "pointer", display: "grid", placeItems: "center" }}>
                        <GripDotsVerticalIcon />
                    </Box>
                </TableCell>
            )}
            {props.renderBody(props.item, props.idx, props.data)}
        </TableRow>
    )
}

export default function DragAndDropTable<T extends {}>(props: DragAndDropTableProps<T>) {
    return (
        <TableContainer component={Paper} variant={props.variant} sx={[{ flex: 1 }, ...(props.sx ? Array.isArray(props.sx) ? props.sx : [props.sx] : [])]}>
            <Table stickyHeader sx={{ maxWidth: "100%" }}>
                <TableHead>
                    <TableRow>
                        {props.dnd && <TableCell padding="checkbox" />}
                        {props.head}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.data.map((item, idx, data) => (
                        <DragAndDropTableRow
                            data={data}
                            idx={idx}
                            item={item}
                            onDrop={props.onDrop}
                            renderBody={props.renderBody}
                            dnd={props.dnd}
                            key={`row-${idx}`}
                        />
                    ))}
                </TableBody>
                {props.children}
            </Table>
        </TableContainer>
    )
}